/**
 * Engraving Customizer
 * Handles all engraving customization interactions and preview rendering
 * Font: Pinyon Script Regular (fixed)
 * Auto-wraps text at 110px width with left/right padding
 */

class EngravingCustomizer {
  constructor(modalElement) {
    this.modal = modalElement;
    this.sectionId = modalElement.id.replace('Engraving-', '');
    this.canvas = modalElement.querySelector(`#engraving-canvas-${this.sectionId}`);
    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

    // Get price from data attribute (passed from Liquid template with product.metafields.custom.engraving_price)
    const priceAttr = modalElement.getAttribute('data-engraving-price');
    let engravingPrice = priceAttr ? parseFloat(priceAttr) : 0;

    // Fallback: check window.productData if available
    const productId = this.sectionId;
    if (window.productData && window.productData[productId] && window.productData[productId].engravingPrice) {
      engravingPrice = parseFloat(window.productData[productId].engravingPrice);
    }

    // State
    this.state = {
      currentLocation: 'back',
      fontSize: 'size7', // 'small4' or 'size7'
      fontFamily: 'Pinyon Script', // Selected font family
      text: '',
      orderNotes: '',
      engravingPrice: engravingPrice,
      hasError: false,
      lastValidText: '' // Store last valid text that fits within boundary
    };

    // Font configuration - now dynamic
    this.fontFamily = "'Pinyon Script', cursive";

    // Visual boundary configuration (red zone limits)
    this.visualBoundary = {
      width: 120,  // Maximum visual width in pixels
      height: 220  // Maximum visual height in pixels
    };

    // Size configurations with individual max text widths
    this.sizeConfig = {
      small4: {
        label: 'Small',
        canvasSize: 32,
        maxTotal: 48,
        maxTextWidth: 110  // Original width for Small
      },
      size7: {
        label: 'Regular',
        canvasSize: 24,
        maxTotal: 48,
        maxTextWidth: 115  // Must be <= visualBoundary.width (120px) to allow wrapped text to fit
      }
    };

    this.init();
  }

  init() {
    if (!this.modal || !this.canvas) return;

    this.cacheElements();
    this.bindEvents();
    this.updatePreview();
  }

  cacheElements() {
    // Location buttons
    this.locationBtns = this.modal.querySelectorAll('.location-btn');

    // Preview background image
    this.backgroundImage = this.modal.querySelector('.preview-product-image');

    // Text controls
    this.textInput = this.modal.querySelector(`#engraving-text-${this.sectionId}`);
    this.orderNotesInput = this.modal.querySelector(`#order-notes-${this.sectionId}`);
    this.charCountDisplay = this.modal.querySelector('.char-count');
    this.maxCharDisplay = this.modal.querySelector('.max-char-count');
    this.sizeOptions = this.modal.querySelectorAll('.size-option');
    this.fontSelect = this.modal.querySelector(`#engraving-font-${this.sectionId}`);
    this.fontPreview = this.modal.querySelector(`#font-preview-${this.sectionId}`);
    this.errorDisplay = this.modal.querySelector('.engraving-error-message');

    // Action buttons
    this.saveBtn = this.modal.querySelector('.engraving-save');
    this.cancelBtn = this.modal.querySelector('.engraving-cancel');
    this.closeBtn = this.modal.querySelector('.engraving-modal__close');

    // Hidden inputs for form data
    this.hiddenDataInput = document.querySelector(`#engraving-data-${this.sectionId}`); // Full JSON for cart
    this.hiddenDisplayInput = document.querySelector(`#engraving-display-${this.sectionId}`); // Formatted for orders
    this.hiddenPreviewInput = document.querySelector(`#engraving-preview-${this.sectionId}`); // Preview image
    this.hiddenTagInput = document.querySelector(`#engraving-tag-${this.sectionId}`); // Order type tag for searching
  }

  bindEvents() {
    // Location switching
    this.locationBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.switchLocation(e.currentTarget.dataset.location));
    });

    // Text input with real-time validation
    if (this.textInput) {
      this.textInput.addEventListener('input', (e) => {
        this.handleTextInput(e.target.value);
      });
    }

    // Order notes input
    if (this.orderNotesInput) {
      this.orderNotesInput.addEventListener('input', (e) => {
        this.state.orderNotes = e.target.value;
      });
    }

    // Size selection
    this.sizeOptions.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.selectSize(e.currentTarget.dataset.size);
      });
    });

    // Font selection
    if (this.fontSelect) {
      this.fontSelect.addEventListener('change', (e) => {
        this.selectFont(e.target.value);
      });
    }

    // Save button
    // Skip binding for cart modal - CartEngravingAdapter handles it
    if (this.saveBtn && this.sectionId !== 'cart') {
      this.saveBtn.addEventListener('click', () => this.saveEngraving());
    }

    // Cancel/Close buttons
    [this.cancelBtn, this.closeBtn].forEach(btn => {
      if (btn) {
        btn.addEventListener('click', () => this.closeModal());
      }
    });
  }

  handleTextInput(value) {
    // Validate for special symbols
    const hasSpecialSymbols = this.containsSpecialSymbols(value);

    if (hasSpecialSymbols) {
      this.showError('Special symbols are not supported.');
      this.state.hasError = true;
      // Restore last valid text
      this.state.text = this.state.lastValidText;
      if (this.textInput) {
        this.textInput.value = this.state.lastValidText;
      }
      return;
    }

    this.state.text = value;

    // Update font preview text
    this.updateFontPreviewText();

    // Update preview first to calculate text bounds
    this.updatePreview();

    // Validate visual boundary after rendering
    const exceedsBoundary = this.checkVisualBoundary();

    if (exceedsBoundary) {
      this.showError('Less is more — try a shorter message.');
      this.state.hasError = true;

      // Show red boundary for 300ms before restoring text
      setTimeout(() => {
        // this.state.text = this.state.lastValidText;
        // if (this.textInput) {
        //   this.textInput.value = this.state.lastValidText;
        // }
        // Update preview with valid text
        this.updatePreview();
        this.updateBoundaryStatus();
      }, 300);
    } else {
      this.clearError();
      this.state.hasError = false;
      // Save this as the last valid text
      this.state.lastValidText = value;
    }

    this.updateBoundaryStatus();
  }

  containsSpecialSymbols(text) {
    // Allow: letters, numbers, spaces, common punctuation, newlines
    // Block: special symbols like @#$%^&*{}[]|\ etc.
    const allowedPattern = /^[a-zA-Z0-9\s.,!?'"\-\n]*$/;
    return !allowedPattern.test(text);
  }

  updateCharCount() {
    const maxTotal = this.state.fontSize === 'small4'
      ? this.sizeConfig.small4.maxTotal
      : this.sizeConfig.size7.maxTotal;

    if (this.charCountDisplay) {
      this.charCountDisplay.textContent = this.state.text.length;
    }
    if (this.maxCharDisplay) {
      this.maxCharDisplay.textContent = maxTotal;
    }

    // Update limit info display
    const limitInfo = this.modal.querySelector('.limit-info');
    if (limitInfo) {
      limitInfo.textContent = `Auto-wraps at 110px width, all text centered`;
    }
  }

  /**
   * Check if rendered text exceeds visual boundary
   * @returns {boolean} - True if text exceeds boundary
   */
  checkVisualBoundary() {
    if (!this.state.text || !this.ctx) return false;

    // Calculate text bounds
    const bounds = this.calculateTextBounds();

    // Check if exceeds boundary
    return bounds.width > this.visualBoundary.width ||
           bounds.height > this.visualBoundary.height;
  }

  /**
   * Calculate bounding box of rendered text
   * @returns {Object} - {width, height} of text bounding box
   */
  calculateTextBounds() {
    if (!this.ctx || !this.state.text) {
      return { width: 0, height: 0 };
    }

    // Get font size from config
    const sizeConfig = this.state.fontSize === 'small4'
      ? this.sizeConfig.small4
      : this.sizeConfig.size7;
    const fontSize = sizeConfig.canvasSize;

    // Set font to match rendering
    this.ctx.font = `${fontSize}px ${this.fontFamily}`;

    // Get wrapped lines using size-specific max width
    const lines = this.wrapText(this.state.text, sizeConfig.maxTextWidth);

    // Calculate width (maximum line width)
    let maxWidth = 0;
    lines.forEach(line => {
      const metrics = this.ctx.measureText(line);
      maxWidth = Math.max(maxWidth, metrics.width);
    });

    // Calculate height (line count * line height)
    const lineHeight = fontSize * 1.2;
    const totalHeight = lines.length * lineHeight;

    return {
      width: maxWidth,
      height: totalHeight
    };
  }

  /**
   * Update boundary status display
   */
  updateBoundaryStatus() {
    const bounds = this.calculateTextBounds();
    const exceedsBoundary = this.checkVisualBoundary();

    // Update character count display
    if (this.charCountDisplay) {
      const currentCount = this.state.text.length;
      this.charCountDisplay.textContent = currentCount;
      this.charCountDisplay.style.color = exceedsBoundary ? '#e74c3c' : '#27ae60';
    }

    // Update limit info
    const limitInfo = this.modal.querySelector('.limit-info');
    if (limitInfo) {
      const widthPct = Math.round((bounds.width / this.visualBoundary.width) * 100);
      const heightPct = Math.round((bounds.height / this.visualBoundary.height) * 100);
      limitInfo.textContent = `Size: ${Math.round(bounds.width)}×${Math.round(bounds.height)}px (max: ${this.visualBoundary.width}×${this.visualBoundary.height}px)`;
    }
  }

  selectSize(size) {
    this.state.fontSize = size;

    this.sizeOptions.forEach(btn => {
      const isActive = btn.dataset.size === size;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    // Re-validate current text
    if (this.state.text) {
      this.handleTextInput(this.state.text);
    } else {
      this.updateBoundaryStatus();
      this.updatePreview();
    }
  }

  selectFont(fontFamily) {
    this.state.fontFamily = fontFamily;

    // Use appropriate fallback font family
    if (fontFamily === 'Arial') {
      this.fontFamily = `'${fontFamily}', sans-serif`;
    } else {
      this.fontFamily = `'${fontFamily}', cursive`;
    }

    // Update text input font
    if (this.textInput) {
      this.textInput.style.fontFamily = this.fontFamily;
    }

    // Update font preview text (only .font-text uses selected font)
    if (this.fontPreview) {
      this.updateFontPreviewText();
    }

    // Re-validate current text and update preview
    if (this.state.text) {
      this.handleTextInput(this.state.text);
    } else {
      this.updatePreview();
    }
  }

  updateFontPreviewText() {
    if (!this.fontPreview) return;
    const fontNameEl = this.fontPreview.querySelector('.font-name');
    const fontTextEl = this.fontPreview.querySelector('.font-text');
    if (fontNameEl) fontNameEl.textContent = this.state.fontFamily || 'Pinyon Script';
    if (fontTextEl) {
      fontTextEl.textContent = 'Sample Text Preview';
      fontTextEl.style.fontFamily = this.state.fontFamily === 'Arial' ? 'Arial, sans-serif' : "'Pinyon Script', cursive";
      fontTextEl.style.fontSize = this.state.fontFamily === 'Arial' ? '18px' : "18px";
    }
  }

  switchLocation(location) {
    this.state.currentLocation = location;

    this.locationBtns.forEach(btn => {
      const isActive = btn.dataset.location === location;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    this.updatePreview();
  }

  updatePreview() {
    if (!this.ctx) return;

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Adjust centerX to match watch back engraving area (slightly right of center)
    const centerX = (this.canvas.width / 2) + 5;
    const centerY = (this.canvas.height / 2) - 10; // Move up 10px

    // Draw visual boundary (red zone)
    this.drawVisualBoundary(centerX, centerY);

    // Set common text styles
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillStyle = '#2c2d2e';

    if (this.state.text) {
      this.renderText(centerX, centerY);
    }
  }

  /**
   * Draw visual boundary rectangle on canvas
   * Red when text exceeds, gray when within bounds
   */
  drawVisualBoundary(centerX, centerY) {
    const exceedsBoundary = this.checkVisualBoundary();

    // Set boundary style
    this.ctx.strokeStyle = exceedsBoundary ? '#e74c3c' : '#dcdcdc';
    this.ctx.lineWidth = exceedsBoundary ? 2 : 1;
    this.ctx.setLineDash(exceedsBoundary ? [] : [5, 5]); // Solid if exceeds, dashed if within

    const offsetY = 10; // 垂直偏移量（正数向下）
    // Draw rectangle centered on canvas
    const rectX = centerX - (this.visualBoundary.width / 2);
    const rectY = centerY - (this.visualBoundary.height / 2) + offsetY;

    this.ctx.strokeRect(rectX, rectY, this.visualBoundary.width, this.visualBoundary.height);

    // Reset line dash for text rendering
    this.ctx.setLineDash([]);
  }

  renderText(x, y) {
  // Get font size from config
  const sizeConfig = this.state.fontSize === 'small4'
    ? this.sizeConfig.small4
    : this.sizeConfig.size7;
  const fontSize = sizeConfig.canvasSize;
  
  // Set font - fixed to Pinyon Script
  this.ctx.font = `${fontSize}px ${this.fontFamily}`;
  
  // Auto-wrap text using size-specific max width
  const text = this.state.text;
  const lines = this.wrapText(text, sizeConfig.maxTextWidth);
  
  // ============ 修改文本渲染位置 ============
  // 与边界相同的偏移量
  const offsetX = 0; // 与边界相同的水平偏移
  const offsetY = 10; // 与边界相同的垂直偏移
  
  const textX = x + offsetX;
  const textY = y + offsetY;
  // ========================================
  
  // Draw each line centered
  const lineHeight = fontSize * 1.2;
  const startY = textY - ((lines.length - 1) * lineHeight) / 2;
  
  lines.forEach((line, index) => {
    this.ctx.fillText(line, textX, startY + index * lineHeight);
  });
  }

  /**
   * Wrap text at specified pixel width
   * Preserves manual line breaks (\n) and blank lines
   * @param {string} text - Text to wrap
   * @param {number} maxWidth - Maximum width in pixels
   * @returns {Array<string>} - Array of text lines
   */
  wrapText(text, maxWidth) {
    // First split by manual line breaks (\n)
    const manualLines = text.split('\n');
    const wrappedLines = [];

    manualLines.forEach(lineText => {
      // Preserve empty lines (blank lines from double Enter)
      if (lineText.trim() === '') {
        wrappedLines.push('');
        return;
      }

      // Apply auto-wrapping to this line segment
      const words = lineText.split(' ');
      let currentLine = '';

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const metrics = this.ctx.measureText(testLine);
        const testWidth = metrics.width;

        if (testWidth > maxWidth && currentLine !== '') {
          // Current line is full, start new line
          wrappedLines.push(currentLine);
          currentLine = word;

          // Check if the single word itself is too long
          const wordWidth = this.ctx.measureText(word).width;
          if (wordWidth > maxWidth) {
            // Break long word by characters
            const brokenWord = this.breakLongWord(word, maxWidth);
            wrappedLines.push(...brokenWord.slice(0, -1)); // Add all complete lines
            currentLine = brokenWord[brokenWord.length - 1]; // Keep last partial line
          }
        } else if (testWidth > maxWidth) {
          // First word is too long, break it by characters
          const brokenWord = this.breakLongWord(word, maxWidth);
          wrappedLines.push(...brokenWord.slice(0, -1));
          currentLine = brokenWord[brokenWord.length - 1];
        } else {
          currentLine = testLine;
        }
      }

      // Add the last line of this segment
      if (currentLine) {
        wrappedLines.push(currentLine);
      }
    });

    return wrappedLines.length > 0 ? wrappedLines : [''];
  }

  /**
   * Break a long word that exceeds maxWidth into multiple lines
   * @param {string} word - The word to break
   * @param {number} maxWidth - Maximum width in pixels
   * @returns {Array<string>} - Array of broken word segments
   */
  breakLongWord(word, maxWidth) {
    const lines = [];
    let currentLine = '';

    for (let i = 0; i < word.length; i++) {
      const char = word[i];
      const testLine = currentLine + char;
      const testWidth = this.ctx.measureText(testLine).width;

      if (testWidth > maxWidth && currentLine !== '') {
        lines.push(currentLine);
        currentLine = char;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [''];
  }

  /**
   * Generate preview image as data URL (with background + text)
   * @returns {string} - Canvas image as data URL
   */
  getPreviewImage() {
    if (!this.canvas || !this.backgroundImage) return '';

    // Wait for background image to load if not loaded yet
    if (!this.backgroundImage.complete) {
      console.warn('Background image not loaded yet, returning text-only preview');
      return this.canvas.toDataURL('image/png');
    }

    try {
      // Create temporary canvas using background image's natural size
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.backgroundImage.naturalWidth;
      tempCanvas.height = this.backgroundImage.naturalHeight;
      const tempCtx = tempCanvas.getContext('2d');

      // Draw background image at its natural size (no scaling)
      tempCtx.drawImage(this.backgroundImage, 0, 0);

      // Calculate scaling factor to overlay text canvas
      const scaleX = tempCanvas.width / this.canvas.width;
      const scaleY = tempCanvas.height / this.canvas.height;

      // Draw text canvas scaled to match background
      tempCtx.drawImage(this.canvas, 0, 0, this.canvas.width * scaleX, this.canvas.height * scaleY);

      // Return merged image as data URL
      return tempCanvas.toDataURL('image/png');
    } catch (error) {
      console.error('Failed to merge background with text:', error);
      // Fallback to text-only canvas
      return this.canvas.toDataURL('image/png');
    }
  }

  /**
   * Upload preview image to imgbb
   * @param {string} base64Image - Base64 encoded image
   * @returns {Promise<string|null>} - Image URL or null if failed
   */
  async uploadPreviewImage(base64Image) {
    // imgbb API key - 免费版每月 5000 张图片上传限制
    const IMGBB_API_KEY = 'f11571ce27757bac05f4b0cfefd3b9c5';

    if (!base64Image) return null;

    try {
      // Remove data URL prefix
      const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, '');

      // Prepare form data
      const formData = new FormData();
      formData.append('image', base64Data);

      // Upload to imgbb
      const response = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success && result.data && result.data.url) {
        return result.data.url;
      } else {
        console.error('imgbb upload failed:', result);
        return null;
      }
    } catch (error) {
      console.error('Failed to upload preview image:', error);
      return null;
    }
  }

saveEngraving() {
  // Validate that text is entered and no errors
  if (!this.state.text) {
    alert('Please enter text for engraving.');
    return;
  }

  if (this.state.hasError) {
    return;
  }

  // Prepare engraving data
  const engravingData = this.getEngravingData();
  if (!engravingData) {
    return;
  }

  // Generate preview URL
  const previewURL = this.generatePreviewURL();

  // Store in hidden inputs
  // 1. Full JSON data for cart functionality (hidden from Shopify admin with _ prefix)
  if (this.hiddenDataInput) {
    const fullData = {
      ...engravingData,
      previewURL: previewURL
    };
    this.hiddenDataInput.value = JSON.stringify(fullData);
  }

  // 2. JSON data for cart display (content, fontFamily, fontSizeLabel)
  if (this.hiddenDisplayInput) {
    const displayData = {
      content: engravingData.content,
      fontFamily: engravingData.fontFamily,
      fontSizeLabel: engravingData.fontSizeLabel
    };
    this.hiddenDisplayInput.value = JSON.stringify(displayData);
  }

  // 3. Store preview URL (instant, no upload needed)
  if (this.hiddenPreviewInput && previewURL) {
    this.hiddenPreviewInput.value = previewURL;
  }

  // 4. Set order type tag for easy searching in Shopify admin
  if (this.hiddenTagInput) {
    this.hiddenTagInput.value = 'ENGRAVING';
  }

  // ============ 新增：提交后清除参数的逻辑 ============
  // 保存当前状态用于后续清除
  const savedState = {
    text: this.state.text,
    fontSize: this.state.fontSize,
    location: this.state.currentLocation,
    orderNotes: this.state.orderNotes
  };
  
  // 设置一个标记，表示已提交
  this.hasSubmitted = true;
  
  // 立即重置当前状态（不影响提交的数据）
  this.resetCurrentStateOnly();
  
  // 设置定时器，在3秒后清除隐藏输入框
  setTimeout(() => {
    this.clearHiddenInputs();
  }, 3000);
  // =================================================

  // Proceed to cart immediately
  this.proceedToCart();
}

// 新增方法：仅重置当前状态（不重置隐藏输入框）
resetCurrentStateOnly() {
  // 只重置状态变量，不清除隐藏输入框
  this.state.text = '';
  this.state.orderNotes = '';
  this.state.lastValidText = '';
  this.state.hasError = false;
  
  // 重置输入框显示
  if (this.textInput) this.textInput.value = '';
  if (this.orderNotesInput) this.orderNotesInput.value = '';
  this.clearError();
  this.updateBoundaryStatus();
  this.updatePreview();
}

// 新增方法：清除隐藏输入框
clearHiddenInputs() {
  console.log('Clearing engraving hidden inputs...');
  
  const inputsToClear = [
    this.hiddenDataInput,
    this.hiddenDisplayInput,
    this.hiddenPreviewInput,
    this.hiddenTagInput
  ];
  
  inputsToClear.forEach(input => {
    if (input) {
      input.value = '';
    }
  });
}

  proceedToCart() {
    // Close modal first
    this.closeModal();

    // Use AJAX add to cart with cart drawer instead of form submission
    // Try to get form ID from data attribute first (preferred), fallback to sectionId
    const formId = this.modal.getAttribute('data-product-form-id') || `product-form-${this.sectionId}`;
    const productForm = document.getElementById(formId);
    if (!productForm) {
      console.error('[EngravingCustomizer] Product form not found, tried:', formId);
      return;
    }

    // Show loading state on Add to Cart button
    const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
    submitButtons.forEach((btn) => {
      btn.setAttribute('aria-disabled', true);
      btn.classList.add('loading');
    });

    // Collect form data
    const formData = new FormData(productForm);

    // Add sections to render for cart drawer update
    const sectionsToRender = ['cart-drawer', 'cart-bubble', 'main-cart'];
    formData.append('sections', sectionsToRender.join(','));
    formData.append('sections_url', window.location.pathname);

    // Fetch config
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    };

    // Get cart add URL from theme or use default
    const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';

    console.log('[EngravingCustomizer] Adding to cart with AJAX...');

    fetch(cartAddUrl, config)
      .then((response) => response.json())
      .then((response) => {
        console.log('[EngravingCustomizer] Cart add response:', response);

        if (response.status) {
          // Error occurred
          console.error('[EngravingCustomizer] Cart add error:', response.description);
          alert(response.description || 'Error adding to cart');
          return;
        }

        // Update cart drawer content
        if (response.sections) {
          this.updateCartDrawerSections(response.sections);
        }

        // Open the cart drawer
        this.openCartDrawer();

        // Dispatch cart:item-added event (but NOT cart:refresh since updateCartDrawerSections already updated the HTML)
        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        }
      })
      .catch((error) => {
        console.error('[EngravingCustomizer] Cart add failed:', error);
        alert('Failed to add to cart. Please try again.');
      })
      .finally(() => {
        // Remove loading state
        submitButtons.forEach((btn) => {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-disabled');
        });
      });
  }

  updateCartDrawerSections(sections) {
    console.log('[EngravingCustomizer] Updating cart drawer sections...');

    const sectionMappings = [
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
      { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' }
    ];

    sectionMappings.forEach((mapping) => {
      const sectionHTML = sections[mapping.section];
      if (!sectionHTML) {
        console.log('[EngravingCustomizer] No HTML for section:', mapping.section);
        return;
      }

      const targetElement = document.getElementById(mapping.id);
      if (!targetElement) {
        console.log('[EngravingCustomizer] Target element not found:', mapping.id);
        return;
      }

      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(sectionHTML, 'text/html');

      // Try to find the selector within the parsed HTML
      let newContent = parsedDoc.querySelector(mapping.selector);

      // If not found directly, try within .shopify-section wrapper
      if (!newContent) {
        const wrapper = parsedDoc.querySelector('.shopify-section');
        if (wrapper) {
          newContent = wrapper.querySelector(mapping.selector);
        }
      }

      if (newContent) {
        const elementToUpdate = targetElement.querySelector(mapping.selector) || targetElement;
        elementToUpdate.innerHTML = newContent.innerHTML;
        console.log('[EngravingCustomizer] Updated section:', mapping.id);
      } else {
        console.log('[EngravingCustomizer] Could not find selector in section:', mapping.selector);
      }
    });

    // Re-initialize cart drawer if available
    if (typeof CartDrawer !== 'undefined') {
      new CartDrawer();
    }
  }

  openCartDrawer() {
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer) {
      document.body.classList.add('open-cc');
      cartDrawer.classList.add('active');

      // Dispatch cart drawer open event
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('cart-drawer:open');
      } else {
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      }

      console.log('[EngravingCustomizer] Cart drawer opened');
    } else {
      console.warn('[EngravingCustomizer] Cart drawer element not found, redirecting to cart');
      window.location.href = '/cart';
    }
  }

  getEngravingData() {
    // Return null if no text entered
    if (!this.state.text) {
      return null;
    }

    // Return null if validation errors
    if (this.state.hasError) {
      return null;
    }

    console.log("this.state.fontSize------------------",this.state.fontSize)

    // Prepare engraving data
    return {
      location: this.state.currentLocation,
      type: 'text',
      content: this.state.text,
      fontSize: this.state.fontSize,
      fontSizeLabel: this.state.fontSize === 'size7' ? 'Small' : 'Regular',
      fontFamily: this.state.fontFamily,
      maxTextWidth: this.maxTextWidth,
      price: this.state.engravingPrice,
      orderNotes: this.state.orderNotes,
      previewURL: this.generatePreviewURL()
    };
  }

  /**
   * Generate preview URL for sharing/viewing
   * @returns {string} - URL to preview page with parameters
   */
  generatePreviewURL() {
    if (!this.state.text) return '';

    // Build URL parameters
    const params = new URLSearchParams();
    params.set('text', this.state.text);
    params.set('fontSize', this.state.fontSize);
    params.set('font', this.state.fontFamily);
    params.set('location', this.state.currentLocation);

    if (this.state.orderNotes) {
      params.set('notes', this.state.orderNotes);
    }

    // Use current domain + /pages/engraving-preview
    const baseUrl = `${window.location.protocol}//${window.location.host}/pages/engraving-preview`;
    return `${baseUrl}?${params.toString()}`;
  }

  loadEngravingData(data) {
    if (!data) return;

    // Load state from data
    this.state.currentLocation = data.location || 'back';
    this.state.fontSize = data.fontSize || 'size7';
    this.state.fontFamily = data.fontFamily || 'Pinyon Script';
    this.state.text = data.content || '';
    this.state.orderNotes = data.orderNotes || '';

    // Update font family with appropriate fallback
    if (this.state.fontFamily === 'Arial') {
      this.fontFamily = `'${this.state.fontFamily}', sans-serif`;
    } else {
      this.fontFamily = `'${this.state.fontFamily}', cursive`;
    }

    // Update UI
    if (this.textInput) {
      this.textInput.value = this.state.text;
      this.textInput.style.fontFamily = this.fontFamily;
    }

    if (this.orderNotesInput) {
      this.orderNotesInput.value = this.state.orderNotes;
    }

    // Update font select
    if (this.fontSelect) {
      this.fontSelect.value = this.state.fontFamily;
    }

    // Update font preview (only .font-text uses selected font)
    this.updateFontPreviewText();

    // Update location buttons
    this.locationBtns.forEach(btn => {
      const isActive = btn.dataset.location === this.state.currentLocation;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    // Update size buttons
    this.sizeOptions.forEach(btn => {
      const isActive = btn.dataset.size === this.state.fontSize;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    // Re-validate and update preview
    this.handleTextInput(this.state.text);
  }

  showError(message) {
    if (this.errorDisplay) {
      this.errorDisplay.textContent = message;
      this.errorDisplay.style.display = 'block';
    }
    if (this.textInput) {
      this.textInput.classList.add('has-error');
    }
    if (this.saveBtn) {
      this.saveBtn.disabled = true;
    }
  }

  clearError() {
    if (this.errorDisplay) {
      this.errorDisplay.textContent = '';
      this.errorDisplay.style.display = 'none';
    }
    if (this.textInput) {
      this.textInput.classList.remove('has-error');
    }
    if (this.saveBtn) {
      this.saveBtn.disabled = false;
    }
  }

  showNotification(message) {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = 'engraving-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #2c2d2e;
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 10000;
      animation: slideUp 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideDown 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  closeModal() {
    this.modal.classList.remove('open');
    document.body.classList.remove('overflow-hidden');
  }

  reset() {
    // Preserve original engraving price from data attribute
    const originalPrice = this.state.engravingPrice;
    this.state = {
      currentLocation: 'back',
      fontSize: 'size7',
      fontFamily: 'Pinyon Script',
      text: '',
      orderNotes: '',
      engravingPrice: originalPrice,
      hasError: false
    };

    // Reset font family property
    this.fontFamily = "'Pinyon Script', cursive";

    // Reset font select dropdown
    if (this.fontSelect) {
      this.fontSelect.value = 'Pinyon Script';
    }

    // Reset text input font
    if (this.textInput) {
      this.textInput.value = '';
      this.textInput.style.fontFamily = this.fontFamily;
    }

    if (this.orderNotesInput) this.orderNotesInput.value = '';

    // Update font preview
    this.updateFontPreviewText();

    this.clearError();
    this.updateBoundaryStatus();
    this.updatePreview();

    // Reset size buttons
    this.sizeOptions.forEach(btn => {
      const isSize7 = btn.dataset.size === 'size7';
      btn.classList.toggle('active', isSize7);
      btn.setAttribute('aria-pressed', isSize7);
    });
  }
}

// Expose class globally for cart engraving adapter
window.EngravingCustomizer = EngravingCustomizer;

// Initialize customizers when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const modals = document.querySelectorAll('.engraving-customizer-modal');

  modals.forEach(modal => {
    const customizer = new EngravingCustomizer(modal);

    // Store reference on modal element
    modal.engravingCustomizer = customizer;
  });

  // Handle modal openers
  const openers = document.querySelectorAll('[data-modal^="#Engraving-"]');
  openers.forEach(opener => {
    opener.addEventListener('click', (e) => {
      e.preventDefault();
      const modalId = opener.dataset.modal.replace('#', '');
      const modal = document.getElementById(modalId);

      if (modal) {
        modal.classList.add('open');
        document.body.classList.add('overflow-hidden');

        // Reset customizer if needed
        if (modal.engravingCustomizer) {
          modal.engravingCustomizer.reset();
        }
      }
    });
  });
});

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideUp {
    from {
      transform: translateX(-50%) translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideDown {
    from {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }
    to {
      transform: translateX(-50%) translateY(20px);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);
